import { Subscription } from 'rxjs';
import { FormGroup, FormArray, FormControl, Validators } from '@angular/forms';

import { OfferProduct, Offer, FeasibilityStatus, ProductRef, BocOpt } from './product';
import { offerProductRefEquals } from './product-utils';

function convertNullToUndefined<T>(value: T): T | undefined {
	if (value === null) {
		return undefined;
	}
	return value;
}

class FormProduct extends FormGroup {
	readonly product: OfferProduct;
	constructor(product: OfferProduct) {
		super({
			// positive number
			quantity: new FormControl(product.quantity, [Validators.required, Validators.min(0.001)]),
			// string of MonthWeekValue
			rdd: new FormControl(product.rdd, Validators.required),
			// string
			deliveryMill: new FormControl(
				product.deliveryMill || 'eoffer.mill.supplier.choice'
			),
			feasibility: new FormControl(product.feasibilityStatus)
		});
		this.product = product;
	}

	getFormQuantity() {
		return <FormControl> this.get('quantity');
	}

	getFormRdd() {
		return <FormControl> this.get('rdd');
	}

	getFormDeliveryMill() {
		return <FormControl> this.get('deliveryMill');
	}

	getFormFeasibility() {
		return <FormControl> this.get('feasibility');
	}

	getProduct() {
		this.product.quantity = convertNullToUndefined(
			<number | undefined> this.getFormQuantity().value
		);
		this.product.rdd = convertNullToUndefined(
			<string | undefined> this.getFormRdd().value
		);
		this.product.deliveryMill = convertNullToUndefined(
			<string | undefined> this.getFormDeliveryMill().value
		);
		if (this.product.deliveryMill === 'eoffer.mill.supplier.choice') {
			this.product.deliveryMill = undefined;
		}
		const feasibilityStatus = convertNullToUndefined(
			<FeasibilityStatus> this.getFormFeasibility().value
		);
		this.product.feasibilityStatus = feasibilityStatus || 'PENDING';
		return this.product;
	}

	addProduct(product: OfferProduct) {
		if (product.quantity !== undefined) {
			this.product.quantity = this.product.quantity === undefined
				? product.quantity
				: this.product.quantity + product.quantity;
			this.getFormQuantity().setValue(this.product.quantity);
		}
		if (this.product.rdd === undefined && product.rdd !== undefined) {
			this.product.rdd = product.rdd;
			this.getFormRdd().setValue(this.product.rdd);
		}
		if (this.product.deliveryMill === undefined && product.deliveryMill !== undefined) {
			this.product.deliveryMill = product.deliveryMill;
			this.getFormDeliveryMill().setValue(this.product.deliveryMill);
		}
	}
}

class FormProducts extends FormArray {
	// parent could decomposed into children, child keeps ref to its parent

	private readonly subscription?: Subscription;
	private readonly parentProducts: FormProducts | undefined;
	constructor(parentProducts?: FormProducts) {
		super([], Validators.minLength(1));
		this.parentProducts = parentProducts;
		if (!this.parentProducts) {
			return;
		}
		this.subscription = this.valueChanges.subscribe(() => {
			if (this.parentProducts) {
				this.parentProducts.updateValueAndValidity();
			}
		});
	}

	isValid() {
		if (this.length === 0) {
			return false;
		}
		for (let i = 0; i < this.length; i++) {
			if (this.getFormProductAt(i).invalid) {
				return false;
			}
		}
		return true;
	}

	getFormProductAt(index: number) {
		return <FormProduct> this.at(index);
	}

	getFormProduct(reference: ProductRef) {
		for (let i = 0; i < this.length; i++) {
			if (offerProductRefEquals(
				this.getFormProductAt(i).product,
				reference
			)) {
				return this.getFormProductAt(i);
			}
		}
		return undefined;
	}

	getFormProducts() {
		return this.controls.map(control => <FormProduct> control);
	}

	isProduct(reference: ProductRef) {
		for (let i = 0; i < this.length; i++) {
			if (offerProductRefEquals(
				this.getFormProductAt(i).product,
				reference
			)) {
				return true;
			}
		}
		return false;
	}

	addProduct(product: OfferProduct) {
		const existingProduct = this.removeProduct(product);
		if (existingProduct) {
			product.characteristics = existingProduct.characteristics;
			product.quantity = existingProduct.quantity;
		}
		this.push(new FormProduct(product));
	}

	removeProduct(reference: ProductRef) {
		for (let i = 0; i < this.length; i++) {
			if (!offerProductRefEquals(
				this.getFormProductAt(i).product,
				reference
			)) {
				continue;
			}
			const productFound = this.getFormProductAt(i).product;
			this.removeAt(i);
			if (this.parentProducts) {
				this.parentProducts.removeProduct(reference);
			}
			return productFound;
		}
		return undefined;
	}

	override clear() {
		if (this.parentProducts) {
			for (let i = 0; i < this.length; i++) {
				this.parentProducts.removeProduct(
					this.getFormProductAt(i).product
				);
			}
		}
		while (this.length > 0) {
			this.removeAt(0);
		}
	}

	getProducts() {
		const products: OfferProduct[] = [];
		for (let i = 0; i < this.length; i++) {
			products.push(this.getFormProductAt(i).getProduct());
		}
		return products.length === 0 ? undefined : products;
	}

	setProducts(products: OfferProduct[]) {
		for (const product of products) {
			this.addProduct(product);
			if (this.parentProducts) {
				this.parentProducts.addProduct(product);
			}
		}
	}

	onDestroy() {
		if (this.subscription) {
			this.subscription.unsubscribe();
		}
	}
}

class FormPeriod extends FormGroup {
	private periodStartMonth: string | undefined;
	private periodEndMonth: string | undefined;
	constructor() {
		super({
			periodStartMonth: new FormControl(undefined, Validators.required),
			periodEndMonth: new FormControl(undefined, Validators.required)
		});
	}

	refreshLastPeriod() {
		this.periodStartMonth = <string | undefined> this.getFormPeriodStartMonth().value;
		this.periodEndMonth = <string | undefined> this.getFormPeriodEndMonth().value;
	}

	getAndUpdateLastPeriodStartMonth(newStartMonth: string | undefined) {
		const last = this.periodStartMonth;
		this.periodStartMonth = newStartMonth;
		return last;
	}

	getAndUpdateLastPeriodEndMonth(newEndMonth: string | undefined) {
		const last = this.periodEndMonth;
		this.periodEndMonth = newEndMonth;
		return last;
	}

	getFormPeriodStartMonth() {
		return <FormControl> this.get('periodStartMonth');
	}

	getFormPeriodEndMonth() {
		return <FormControl> this.get('periodEndMonth');
	}

	getPeriodStartMonth() {
		return convertNullToUndefined(<string | undefined> this.getFormPeriodStartMonth().value);
	}

	getPeriodEndMonth() {
		return convertNullToUndefined(<string | undefined> this.getFormPeriodEndMonth().value);
	}

	setPeriod(start: string | undefined, end: string | undefined) {
		this.periodStartMonth = start;
		this.periodEndMonth = end;
		this.getFormPeriodStartMonth().setValue(start);
		this.getFormPeriodEndMonth().setValue(end);
	}
}
export class FormOffer extends FormGroup {
	private lastSoldTo: string | undefined;
	private lastShipTo: string | undefined;
	private lastEndUser: string | undefined;
	private offerId: number | undefined;
	private externalOfferId: string | undefined;
	constructor() {
		super({
			rfqRef: new FormControl(undefined, [Validators.required, Validators.maxLength(255)]), // string
			soldTo: new FormControl(undefined, Validators.required), // businessId: string
			shipTo: new FormControl(undefined, Validators.required), // businessId: string
			endUser: new FormControl(undefined, Validators.required), // businessId: string
			period: new FormPeriod(),
			idd: new FormControl(undefined, Validators.required), // string (idd.code)
			tmode: new FormControl(undefined, Validators.required), // string (tmode.code)
			incoterm: new FormControl(undefined, Validators.required), // string (incoterm.code)
			pricingUnit: new FormControl(undefined, Validators.required), // string (pricingUnit.key)
			currency: new FormControl(undefined, Validators.required), // string (currency.key)
			comment: new FormControl(),
			favorite: new FormControl(false),
			products: new FormProducts()
		});
	}

	refreshLastBoc() {
		this.lastSoldTo = <string | undefined> this.getFormSoldTo().value;
		this.lastShipTo = <string | undefined> this.getFormShipTo().value;
		this.lastEndUser = <string | undefined> this.getFormEndUser().value;
	}

	getAndUpdateLastBoc(newBoc: BocOpt) {
		const soldTo = this.lastSoldTo;
		this.lastSoldTo = newBoc.soldTo;
		const shipTo = this.lastShipTo;
		this.lastShipTo = newBoc.shipTo;
		const endUser = this.lastEndUser;
		this.lastEndUser = newBoc.endUser;
		return { soldTo, shipTo, endUser };
	}

	getOfferId() {
		return this.offerId;
	}

	setOfferId(offerId: number) {
		this.offerId = offerId;
	}

	getExternalOfferId() {
		return this.externalOfferId;
	}

	setExternalOfferId(externalOfferId: string) {
		this.externalOfferId = externalOfferId;
	}

	getFormRfqRef() {
		return <FormControl> this.get('rfqRef');
	}

	getFormSoldTo() {
		return <FormControl> this.get('soldTo');
	}

	getFormShipTo() {
		return <FormControl> this.get('shipTo');
	}

	getFormEndUser() {
		return <FormControl> this.get('endUser');
	}

	getFormPeriod() {
		return <FormPeriod> this.get('period');
	}

	getFormIdd() {
		return <FormControl> this.get('idd');
	}

	getFormTmode() {
		return <FormControl> this.get('tmode');
	}

	getFormIncoterm() {
		return <FormControl> this.get('incoterm');
	}

	getFormPricingUnit() {
		return <FormControl> this.get('pricingUnit');
	}

	getFormCurrency() {
		return <FormControl> this.get('currency');
	}

	getFormComment() {
		return <FormControl> this.get('comment');
	}

	getFormFavorite() {
		return <FormControl> this.get('favorite');
	}

	getFormProducts() {
		return <FormProducts> this.get('products');
	}

	isOfferHeaderValid() {
		return this.getFormRfqRef().valid
			&& this.getFormSoldTo().valid
			&& this.getFormShipTo().valid
			&& this.getFormEndUser().valid
			&& this.getFormPeriod().valid
			&& this.getFormIdd().valid
			&& this.getFormTmode().valid
			&& this.getFormIncoterm().valid
			&& this.getFormPricingUnit().valid
			&& this.getFormCurrency().valid;
	}

	getOffer() {
		return {
			header: {
				id: this.offerId,
				externalId: this.externalOfferId,
				rfqRef: convertNullToUndefined(<string | undefined> this.getFormRfqRef().value),
				soldTo: convertNullToUndefined(<string | undefined> this.getFormSoldTo().value),
				shipTo: convertNullToUndefined(<string | undefined> this.getFormShipTo().value),
				endUser: convertNullToUndefined(<string | undefined> this.getFormEndUser().value),
				periodStartMonth: this.getFormPeriod().getPeriodStartMonth(),
				periodEndMonth: this.getFormPeriod().getPeriodEndMonth(),
				idd: convertNullToUndefined(<string | undefined> this.getFormIdd().value),
				tmode: convertNullToUndefined(<string | undefined> this.getFormTmode().value),
				incoterm: convertNullToUndefined(<string | undefined> this.getFormIncoterm().value),
				pricingUnit: convertNullToUndefined(<string | undefined> this.getFormPricingUnit().value),
				currency: convertNullToUndefined(<string | undefined> this.getFormCurrency().value),
				comment: convertNullToUndefined(<string | undefined> this.getFormComment().value)
			},
			products: this.getFormProducts().getProducts()
		};
	}

	setOffer(offer: Offer) {
		const { header } = offer;
		this.offerId = header.id;
		this.externalOfferId = header.externalId;
		this.getFormRfqRef().setValue(header.rfqRef);
		this.lastSoldTo = header.soldTo;
		this.getFormSoldTo().setValue(header.soldTo);
		this.lastShipTo = header.shipTo;
		this.getFormShipTo().setValue(header.shipTo);
		this.lastEndUser = header.endUser;
		this.getFormEndUser().setValue(header.endUser);
		this.getFormPeriod().setPeriod(header.periodStartMonth, header.periodEndMonth);
		this.getFormIdd().setValue(header.idd);
		this.getFormTmode().setValue(header.tmode);
		this.getFormIncoterm().setValue(header.incoterm);
		this.getFormPricingUnit().setValue(header.pricingUnit);
		this.getFormCurrency().setValue(header.currency);
		this.getFormProducts().setProducts(offer.products || []);
		this.getFormComment().setValue(header.comment);
		return this;
	}
}
